import axios from "axios";

const API_BASE_URL = "https://risktest.cogitofs.com/api/v1";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
});


export const getInventoryDashboard = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching inventory dashboard:", error);
    throw new Error(
      error.response?.data?.detail || error.message || "Failed to load inventory dashboard"
    );
  }
};

// ========================================
// Forecast
// ========================================

export const getForecast = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/forecast/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching forecast:", error);
    return [];
  }
};

// ========================================
// Festival Forecast
// ========================================

export const getFestivalForecast = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/festival/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching festival forecast:", error);
    return [];
  }
};

// ========================================
// Alerts
// ========================================

export const getAlerts = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/alerts/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching alerts:", error);
    return [];
  }
};

// ========================================
// Inventory Health
// ========================================

export const getInventoryHealth = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/inventory-health/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching inventory health:", error);
    return [];
  }
};

// ========================================
// Purchase Recommendation
// ========================================

export const getPurchaseRecommendation = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/purchase-recommendation/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching purchase recommendations:", error);
    return [];
  }
};

// ========================================
// Expiry Prediction
// ========================================

export const getExpiryPrediction = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/expiry/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching expiry predictions:", error);
    return [];
  }
};

// ========================================
// Dead Stock
// ========================================

export const getDeadStock = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/dead-stock/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching dead stock:", error);
    return [];
  }
};

// ========================================
// Slow Moving
// ========================================

export const getSlowMoving = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/slow-moving/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching slow-moving items:", error);
    return [];
  }
};

// ========================================
// Fast Moving
// ========================================

export const getFastMoving = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/fast-moving/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching fast-moving items:", error);
    return [];
  }
};

// ========================================
// ABC Analysis
// ========================================

export const getAbcAnalysis = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/abc/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching ABC analysis:", error);
    return [];
  }
};

// ========================================
// XYZ Analysis
// ========================================

export const getXyzAnalysis = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/xyz/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching XYZ analysis:", error);
    return [];
  }
};

// ========================================
// Recommendations
// ========================================

export const getRecommendations = async (businessUuid) => {
  try {
    const response = await apiClient.get(`/inventory/recommendations/${businessUuid}`);
    return response.data;
  } catch (error) {
    console.warn("Error fetching recommendations:", error);
    return [];
  }
};

// ========================================
// Purchase Orders
// ========================================

export const getPurchaseOrders = async (businessUuid, status = null) => {
  try {
    const url = status 
      ? `/inventory/purchase-orders/${businessUuid}?status=${status}`
      : `/inventory/purchase-orders/${businessUuid}`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.warn("Error fetching purchase orders:", error);
    return []; // Graceful fallback for dashboard
  }
};

export const getPurchaseOrder = async (businessUuid, purchaseOrderUuid) => {
  try {
    const response = await apiClient.get(
      `/inventory/purchase-orders/${businessUuid}/${purchaseOrderUuid}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching purchase order:", error);
    throw new Error(
      error.response?.data?.detail || error.message || "Failed to fetch purchase order"
    );
  }
};

export const approvePurchaseOrder = async (businessUuid, purchaseOrderUuid, approvedBy) => {
  try {
    const response = await apiClient.put(
      `/inventory/purchase-orders/${businessUuid}/${purchaseOrderUuid}/approve`,
      { approvedBy }
    );
    return response.data;
  } catch (error) {
    console.error("Error approving purchase order:", error);
    throw new Error(
      error.response?.data?.detail || error.message || "Failed to approve purchase order"
    );
  }
};

export const rejectPurchaseOrder = async (businessUuid, purchaseOrderUuid, rejectedBy, reason) => {
  try {
    const response = await apiClient.put(
      `/inventory/purchase-orders/${businessUuid}/${purchaseOrderUuid}/reject`,
      { rejectedBy, reason }
    );
    return response.data;
  } catch (error) {
    console.error("Error rejecting purchase order:", error);
    throw new Error(
      error.response?.data?.detail || error.message || "Failed to reject purchase order"
    );
  }
};

export const receivePurchaseOrder = async (businessUuid, purchaseOrderUuid, receivedBy) => {
  try {
    const response = await apiClient.put(
      `/inventory/purchase-orders/${businessUuid}/${purchaseOrderUuid}/receive`,
      { receivedBy }
    );
    return response.data;
  } catch (error) {
    console.error("Error receiving purchase order:", error);
    throw new Error(
      error.response?.data?.detail || error.message || "Failed to receive purchase order"
    );
  }
};